import React from 'react';
export type BlogStep = {
  number: number;
  title: string;
  image: string;
  description: string;
  tips: string[];
  material?: string;
  materialSlug?: string;
};
export type BlogPost = {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  image: string;
  type: 'guide' | 'article';
  quickInfo?: string[];
  materialsNeeded?: {
    name: string;
    use: string;
    slug: string | null;
  }[];
  steps?: BlogStep[];
  content?: React.ReactNode;
  seoBlocks?: {
    title: string;
    content: React.ReactNode;
  }[];
};
export const blogPosts: BlogPost[] = [{
  slug: 'best-time-regrade-gravel-driveway-seattle',
  title: 'August Is the Best Month to Regrade a Gravel Driveway in Seattle',
  date: 'August 1, 2026',
  excerpt: 'Puget Sound gets roughly six dry weeks a year, and August is the heart of them. Here is why late summer is the only window where crushed rock actually compacts — and what happens if you wait until the rain returns.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/9f693654-2a38-443b-b05e-cbf8adf96a42.jpg',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-1924" data-mp="2ijtjlhg28r14">
        <p className="mb-4" data-id="element-1925" data-mp="24kllkrvmvoyi">
          Every fall we get the same call: a driveway in Shoreline or Renton
          that turned to soup after the first serious rain, and the homeowner
          wants gravel delivered tomorrow. We can absolutely deliver it. But the
          honest answer is that the repair would have cost less, gone faster,
          and lasted years longer if it had happened in August.
        </p>
        <p className="mb-4" data-id="element-1926" data-mp="2nhv4m7x1r0f8">
          Seattle averages under an inch of rain in August and close to six
          inches by November. That difference is not just a comfort issue for
          whoever is running the plate compactor — it decides whether your new
          rock locks together or floats.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1927" data-mp="3kzd2o8nbx9si">
          Why Dry Ground Is the Whole Game
        </h3>
        <p className="mb-4" data-id="element-1928" data-mp="3pufrcf95npwo">
          Crushed minus rock compacts because the stone dust in it fills the
          voids between the angular pieces and binds them into a near-solid
          slab. That only works within a fairly narrow moisture range. Too dry
          and the fines never bind; far more common around here, too wet and the
          sub-grade underneath turns plastic. The compactor pushes rock down
          into mud instead of knitting it together, and you have effectively
          buried a few hundred dollars of material.
        </p>
        <p className="mb-4" data-id="element-1929" data-mp="323abw174mzmw">
          In August the native glacial till under most Puget Sound driveways is
          as firm as it gets all year. You compact onto something solid, the
          layers stay where you put them, and the finished surface has two full
          months of dry weather to settle under traffic before the rain tests
          it.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1930" data-mp="13mrodfm9n600">
          What a Late-Summer Regrade Actually Involves
        </h3>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-1931" data-mp="1cohfslg5e4xs">
          <li data-id="element-1932" data-mp="1coraung1v2ac">
            <strong data-id="element-1933" data-mp="1cm4zsdtya4nd">Re-establish the crown.</strong> The center should sit
            roughly 2 inches higher than the edges over a 10-foot width so water
            sheds sideways instead of running the length of the drive and
            cutting channels.
          </li>
          <li data-id="element-1934" data-mp="ozgcfcm69jzd">
            <strong data-id="element-1935" data-mp="oywv9y4cizme">Fix the potholes properly.</strong> Square up the edges of
            each hole rather than just filling the dish — loose rock dumped into
            a smooth bowl works right back out within a season.
          </li>
          <li data-id="element-1936" data-mp="19cad9omtsah">
            <strong data-id="element-1937" data-mp="19ugqz50dmw4">Top with 5/8" Minus.</strong> Two to three inches of fresh
            surface rock, compacted in a single pass, restores the driving
            surface without rebuilding the base.
          </li>
          <li data-id="element-1938" data-mp="28vvv3fop5ff3">
            <strong data-id="element-1939" data-mp="28vcd5yz1qr12">Check where the water goes.</strong> If runoff from the
            street or a downspout crosses the driveway, no amount of new rock
            will hold. That is a drainage fix, not a gravel fix.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1940" data-mp="2cwo7uiws22g4">
          If the Base Is Gone, Do It Now
        </h3>
        <p className="mb-4" data-id="element-1941" data-mp="2rk0vcd65ai95">
          Surface rutting is a topcoat problem. But if you can feel the driveway
          flex under a vehicle, or ruts return within weeks of being filled, the
          structural base has failed and topping it is throwing good money
          after bad. Rebuilding means 3–4 inches of 1.25" Minus compacted as a
          base, then the 5/8" Minus surface over it. That is a job you want on
          dry ground — attempting it in November roughly doubles the material it
          takes to reach a stable grade.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1942" data-mp="s2pflehdcxn8">
          One Local Wrinkle: Drainage Rock Season
        </h3>
        <p className="mb-4" data-id="element-1943" data-mp="w3iz7vns3qab">
          August is also the practical deadline for French drains and downspout
          trenches. Digging is dramatically easier in dry soil, and a drain
          installed now is proven before the wet season instead of during it. If
          your driveway problem is really a water problem, 7/8" Drain Rock in a
          properly fabric-wrapped trench will do more for it than any amount of
          surface gravel.
        </p>

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1944" data-mp="3vjxvhssjdnfb">
          The Short Version
        </h3>
        <p className="mb-4" data-id="element-1945" data-mp="4dejwbevltfy">
          You have until roughly late September before Puget Sound weather stops
          cooperating. A driveway regraded in August compacts properly, settles
          under two months of dry traffic, and heads into winter with a crown
          that actually sheds water. The same work in November costs more rock
          and delivers a worse result. If you are on the fence, this is the
          month to get it done — call or text dispatch and we will help you
          figure out the yardage before you order.
        </p>
      </div>
}, {
  slug: 'walkway-gravel-vs-driveway-gravel',
  title: 'Walkway Gravel vs Driveway Gravel — Which One Do I Need?',
  date: 'June 26, 2026',
  excerpt: 'They look similar in the bin, but walkway gravel and driveway gravel are built for very different jobs. Here is how to pick the right one so your project lasts.',
  image: 'https://cdn.magicpatterns.com/uploads/ebxsCGdnf9doSY72o8VZYu/file_0000000013d071fd98aa813ad564b1e6.png',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-1946" data-mp="219hiekx8b5vs">
        <p className="mb-4" data-id="element-1947" data-mp="2ysivya1cfm9s">
          Walkway gravel and driveway gravel can look almost identical sitting
          in a bulk bin, but they are engineered for completely different loads.
          A path only has to carry foot traffic; a driveway has to hold up under
          the weight of cars, trucks, and the occasional delivery vehicle.
          Choosing the wrong one means a soft, rutted path or a driveway that
          scatters every time you back out. Here is how to tell them apart.
        </p>

        {/* Replace this image later */}
        <img src="https://cdn.magicpatterns.com/uploads/tpiwXZ7h5VKuZJngK4ptsh/driveway_gravel_spreading_in_tacoma.png" alt="Crushed gravel being spread across a driveway" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-1948" data-mp="3k98fnpv9nyvv" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1949" data-mp="3u1w9cbqy9mqd">
          Walkway Gravel: Comfort & Looks
        </h3>
        <p className="mb-4" data-id="element-1950" data-mp="7tebczry4zv3">
          Walkways prioritize a comfortable surface to walk on and a clean,
          finished appearance. Smaller, rounded, or decorative stone — like 3/8"
          pea gravel or crushed drain rock — feels good underfoot and packs into
          a stable, attractive path. Because a walkway never sees vehicle
          weight, you do not need a heavy structural base; a few inches of
          material over a compacted, weed-blocked sub-grade is usually plenty.
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-1951" data-mp="1259l3ik1ivyw">
          <li data-id="element-1952" data-mp="122exxj9n6x1s">
            <strong data-id="element-1953" data-mp="eedw0t6yyevc">Stone size:</strong> 3/8" pea gravel or small decorative
            rock — smooth and easy on bare feet.
          </li>
          <li data-id="element-1954" data-mp="9gvkcv1khljn">
            <strong data-id="element-1955" data-mp="9hdsaoxldo7e">Depth:</strong> 2–3 inches over landscape fabric.
          </li>
          <li data-id="element-1956" data-mp="o58l05blnskh">
            <strong data-id="element-1957" data-mp="o5rtkvfd14lu">Best for:</strong> garden paths, side yards, patio borders,
            and fire-pit surrounds.
          </li>
        </ul>

        {/* Replace this image later */}
        <img src="https://cdn.magicpatterns.com/uploads/9QXpjprgL7K5DjWViDEHz9/spreading_the_fabric_driveway.png" alt="Landscape fabric laid down before a gravel walkway" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-1958" data-mp="3rs8ufpmzpjma" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1959" data-mp="22alfgmsnbc5v">
          Driveway Gravel: Strength & Stability
        </h3>
        <p className="mb-4" data-id="element-1960" data-mp="1tcrlhiicsulv">
          Driveways need angular, "minus" crushed rock that locks together under
          weight. The fines (stone dust) in a minus product bind the larger
          stones into a firm, near-solid surface once compacted — exactly what
          you want when a vehicle rolls over it. A driveway is built in layers:
          a coarse 1.25" Minus base for structure, topped with a smoother 5/8"
          Minus driving surface that sheds water and resists potholes.
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-1961" data-mp="2gzzr61jp25of">
          <li data-id="element-1962" data-mp="10ncbmw2fd7rj">
            <strong data-id="element-1963" data-mp="10mjd2idmdokh">Base layer:</strong> 1.25" Minus crushed rock, 3–4 inches,
            compacted.
          </li>
          <li data-id="element-1964" data-mp="15n71cwagrrb0">
            <strong data-id="element-1965" data-mp="15nqlmjnl6rs7">Top layer:</strong> 5/8" Minus crushed rock, 2–3 inches, the
            finished driving surface.
          </li>
          <li data-id="element-1966" data-mp="6kpsvt7nryeu">
            <strong data-id="element-1967" data-mp="ajp6e27gh3hw">Best for:</strong> driveways, parking pads, and any surface
            that carries vehicles.
          </li>
        </ul>

        {/* Replace this image later */}
        <img src="https://cdn.magicpatterns.com/uploads/7Nv3L8waV4wtGihPojgZkN/fina_product_gravel_driveway_in_tacoma.png" alt="Finished compacted gravel driveway" className="w-full h-64 md:h-80 object-cover rounded-xl my-6" data-id="element-1968" data-mp="3sw3rx2dt3q2s" />

        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1969" data-mp="3nzd2hog43i4p">
          Quick Answer
        </h3>
        <p className="mb-4" data-id="element-1970" data-mp="3a03uvd5z1wcc">
          If people walk on it, choose a smaller, comfortable stone like 3/8"
          pea gravel. If vehicles drive on it, use angular minus crushed rock in
          two compacted layers. When in doubt, give us a call — we will help you
          pick the right material and the right amount before you order.
        </p>
      </div>
}, {
  slug: 'perfect-gravel-driveway',
  title: 'How to Build a Perfect Gravel Driveway',
  date: 'May 24, 2026',
  excerpt: 'A complete step-by-step guide to transforming a dirt driveway into a professional, long-lasting gravel surface. No contractor needed.',
  image: 'https://cdn.magicpatterns.com/uploads/bH95N6JcLS3ddKehbnVRWv/gravel_landscaping_in_tacoma.png',
  type: 'guide',
  quickInfo: ['⏱ Weekend project', '📏 Any driveway size', '💰 ~$3–5/sq ft', '🔧 Basic tools needed'],
  materialsNeeded: [{
    name: '1.25" Minus Crushed Rock',
    use: 'Base layer — compacts into a solid foundation',
    slug: '1-25-minus'
  }, {
    name: '5/8" Minus Crushed Rock',
    use: 'Top layer — smooth, firm driving surface',
    slug: '5-8-minus'
  }, {
    name: 'Landscape Fabric',
    use: 'Weed barrier between soil and gravel',
    slug: null
  }, {
    name: 'Fill Dirt (if needed)',
    use: 'Level out low spots before gravel',
    slug: null
  }],
  steps: [{
    number: 1,
    title: 'Assess & Measure Your Driveway',
    image: 'https://cdn.magicpatterns.com/uploads/2ebE9ciuiVZZKZHNZLJiZ5/measuring_gravel_driveway.png',
    description: 'Start by measuring the length, width, and noting the current condition of your dirt driveway. Mark the boundaries with stakes and string. Calculate the square footage — this determines how much material you need.',
    tips: ['Measure length × width in feet', 'Plan for 4–6 inches of base material depth', 'Use our Calculator to estimate yards needed', 'Account for any slope or drainage needs']
  }, {
    number: 2,
    title: 'Clear & Grade the Surface',
    image: 'https://cdn.magicpatterns.com/uploads/gQYsAvdSYDiWUkNHGFHmQH/grading_driveway.png',
    description: 'Remove any vegetation, large rocks, and debris from the driveway area. Use a rake or skid steer to grade the surface so water drains to the sides. A slight crown (higher in the center) prevents puddles.',
    tips: ['Remove all grass, weeds, and roots', 'Create a 2% slope from center to edges', 'Fill any low spots with fill dirt', 'Compact the bare soil with a plate compactor']
  }, {
    number: 3,
    title: 'Install Landscape Fabric (Optional)',
    image: 'https://cdn.magicpatterns.com/uploads/9QXpjprgL7K5DjWViDEHz9/spreading_the_fabric_driveway.png',
    description: 'Lay commercial-grade landscape fabric over the graded dirt. This prevents weeds from growing up through your gravel and helps separate the soil from the rock layers. Overlap seams by 6–12 inches.',
    tips: ['Use commercial-grade fabric, not cheap weed barrier', 'Overlap seams by at least 6 inches', 'Secure with landscape staples every 3 feet', 'This step saves major headaches down the road']
  }, {
    number: 4,
    title: 'Spread the Base Layer — 1.25" Minus',
    image: 'https://cdn.magicpatterns.com/uploads/tpiwXZ7h5VKuZJngK4ptsh/driveway_gravel_spreading_in_tacoma.png',
    description: 'The base layer is the foundation of your driveway. Spread 3–4 inches of 1.25" Minus crushed rock evenly across the surface. This coarser material locks together and creates a rock-solid base that won\'t shift.',
    tips: ['Order 1.25" Minus from our catalog', 'Spread evenly with a rake to 3–4" depth', 'Compact thoroughly with a plate compactor', 'Make multiple passes until the surface is firm'],
    material: '1.25" Minus',
    materialSlug: '1-25-minus'
  }, {
    number: 5,
    title: 'Compact the Base Layer',
    image: 'https://cdn.magicpatterns.com/uploads/hkbT29dFvc9BT49e5MH1GV/compacting_base_layer.png',
    description: 'Rent a plate compactor (available at most equipment rental shops) and make several passes over the base layer. Wet the material lightly before compacting — moisture helps the fines bind together for maximum density.',
    tips: ['Lightly wet the gravel before compacting', 'Make 3–4 passes in different directions', 'The surface should feel solid underfoot', 'Add more material to any low spots and re-compact']
  }, {
    number: 6,
    title: 'Add the Top Layer — 5/8" Minus',
    image: 'https://cdn.magicpatterns.com/uploads/pqds5pqkfBofDzHJ4pjSBj/base_layer_compacting.png',
    description: 'Now spread 2–3 inches of 5/8" Minus crushed rock on top. This is the finished driving surface — it packs down tight, sheds water, and provides a smooth, firm surface for vehicles and foot traffic.',
    tips: ['Order 5/8" Minus — our most popular driveway gravel', 'Spread to 2–3" depth over the compacted base', 'Rake smooth and maintain the crown shape', 'Compact one final time for a professional finish'],
    material: '5/8" Minus',
    materialSlug: '5-8-minus'
  }, {
    number: 7,
    title: 'Final Compaction & Edging',
    image: 'https://cdn.magicpatterns.com/uploads/7Nv3L8waV4wtGihPojgZkN/fina_product_gravel_driveway_in_tacoma.png',
    description: 'Give the top layer a final compaction pass. Optionally, install edging (timber, steel, or stone) along the sides to keep gravel contained. Your new driveway is ready to use — it will continue to settle and harden over the first few weeks.',
    tips: ['Edging prevents gravel from migrating into the yard', 'Drive on it gently for the first week', 'Top off with a thin layer of 5/8" Minus annually', 'Keep drainage channels clear of debris']
  }],
  seoBlocks: [{
    title: 'Gravel Delivery Across Pierce & King Counties',
    content: <>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed mb-3" data-id="element-1971" data-mp="w2fngaejccl6">
              Whether you're building a new gravel driveway in Puyallup,
              replacing a muddy parking pad in Bonney Lake, or resurfacing a
              rural road in Enumclaw, having the right materials delivered on
              time makes all the difference. 206Gravel provides same-day and
              next-day bulk gravel delivery from Tacoma to Seattle and
              everywhere in between — Lakewood, Federal Way, Auburn, Kent,
              Sumner, Edgewood, Renton, Bellevue, Kirkland, Redmond, and the
              surrounding Pierce and King County communities.
            </p>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed" data-id="element-1972" data-mp="3995gyc87vwrv">
              Our dump trucks carry 5–12 yards per load and can place material
              directly on your driveway, jobsite, or backyard — no wheelbarrow
              relay required. We know the local soil conditions (hello, Puget
              Sound clay) and can recommend the right base and top layers for
              driveways that last through Pacific Northwest winters.
            </p>
          </>
  }, {
    title: 'Why Buy Gravel Locally in the Puget Sound?',
    content: <>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed mb-3" data-id="element-1973" data-mp="2glst7yqbliwz">
              Big-box stores sell gravel by the bag — but for driveways and
              serious landscaping, you need bulk delivery by the yard. Buying
              locally from 206Gravel means lower delivery costs, fresher
              material sourced from regional quarries, and drivers who know how
              to navigate Tacoma's hills, Seattle's tight streets, King County's
              rural roads, and everything in between.
            </p>
            <p className="text-brand-charcoal/80 dark:text-stone-300 text-sm leading-relaxed" data-id="element-1974" data-mp="3hi1a37nphrw5">
              We're based in the 206 area code and deliver to King County and
              King County daily. No middlemen, no markups from national chains —
              just honest pricing on crushed rock, drain rock, topsoil, bark,
              and recycled aggregates delivered straight to your property.
            </p>
          </>
  }]
}, {
  slug: 'best-base-for-gravel-driveway',
  title: 'Best Base for a Gravel Driveway',
  date: 'May 19, 2026',
  excerpt: 'The best base for a gravel driveway is a large angular crushed stone (around 1 to 2 inches) for the sub-base, laid...',
  image: 'https://cdn.magicpatterns.com/uploads/8BfgzDv9jVyH7TX3BiStiF/Gravel2BDriveway2BHero2BDesktop.webp',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-1975" data-mp="2a76puj2ehppx">
        <p className="mb-4" data-id="element-1976" data-mp="2582i7wv4k01p">
          The foundation of any long-lasting gravel driveway is its base layer.
          Without a proper base, your top gravel will sink into the mud,
          creating potholes and requiring constant maintenance.
        </p>
        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1977" data-mp="2t4ztjnkum3od">
          Why 1.25" Minus is the Standard
        </h3>
        <p className="mb-4" data-id="element-1978" data-mp="2o4vd1xjxx8y5">
          For most residential driveways in the Pacific Northwest, 1.25" Minus
          crushed rock is the go-to base material. The "minus" means it contains
          stone dust (fines) along with the larger 1.25-inch angular rocks. When
          compacted, these fines lock the larger stones together like concrete.
        </p>
        <p className="mb-4" data-id="element-1979" data-mp="36wo3plbjei2p">
          Always ensure you lay down at least 3-4 inches of base rock and
          compact it thoroughly before adding your top layer.
        </p>
      </div>
}, {
  slug: 'how-much-is-a-load-of-gravel',
  title: 'How Much is a Load of Gravel?',
  date: 'May 11, 2026',
  excerpt: 'A standard tandem-axle dump truck load of gravel (around 12 to 15 tons) costs around $400 to $1,000 delivered in 2026...',
  image: 'https://cdn.magicpatterns.com/uploads/pPaGG3aiENovvC8wi26rwz/TJHunt5-700x467.jpg',
  type: 'article',
  content: <div className="prose dark:prose-invert max-w-none text-brand-charcoal dark:text-stone-300" data-id="element-1980" data-mp="30p667nrd7qbz">
        <p className="mb-4" data-id="element-1981" data-mp="2vr4ob65odx23">
          The cost of a load of gravel depends heavily on the material type,
          your location, and the size of the truck delivering it.
        </p>
        <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mt-8 mb-4" data-id="element-1982" data-mp="35mofjf9ao7ki">
          Truck Sizes
        </h3>
        <ul className="list-disc pl-5 mb-4 space-y-2" data-id="element-1983" data-mp="2hwap1t6cygs1">
          <li data-id="element-1984" data-mp="2czu0ixv8y0ef">
            <strong data-id="element-1985" data-mp="2cy0unksgjk7h">Solo Dump Truck:</strong> Carries about 5 to 7 yards (6-9
            tons). Ideal for smaller residential projects.
          </li>
          <li data-id="element-1986" data-mp="sf6tkbhbb5rr">
            <strong data-id="element-1987" data-mp="x7ge1bznrdmn">Tandem-Axle Dump Truck:</strong> Carries about 10 to 12
            yards (12-15 tons). The standard for most driveway installations.
          </li>
          <li data-id="element-1988" data-mp="je8l9gzucc3d">
            <strong data-id="element-1989" data-mp="nhwsfykk95o3">Truck and Pup:</strong> Carries up to 24 yards. Best for
            large rural driveways or commercial jobs.
          </li>
        </ul>
        <p className="mb-4" data-id="element-1990" data-mp="1b8sr9fnfp14u">
          Remember that delivery fees are often a significant portion of the
          total cost, which is why buying locally from a supplier in your
          delivery zone is crucial.
        </p>
      </div>
}];